import { customElement, DeesElement, property, html, css, cssManager, type TemplateResult } from '@design.estate/dees-element';
import { demoFunc } from './dees-shopping-productcard.demo.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-input/dees-input-checkbox/dees-input-checkbox.js';
import '../../00group-input/dees-input-quantityselector/dees-input-quantityselector.js';

export interface IProductData {
  name: string;
  category?: string;
  description?: string;
  price: number;
  originalPrice?: number;
  currency?: string;
  inStock?: boolean;
  stockText?: string;
  imageUrl?: string;
  iconName?: string;
}

declare global { interface HTMLElementTagNameMap { 'dees-shopping-productcard': DeesShoppingProductcard; } }

@customElement('dees-shopping-productcard')
export class DeesShoppingProductcard extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Simple'];
  @property({ type: Object }) accessor productData: IProductData = { name: 'Product Name', price: 0 };
  @property({ type: Number }) accessor quantity = 0;
  @property({ type: Boolean }) accessor showQuantitySelector = true;
  @property({ type: Boolean }) accessor selectable = false;
  @property({ type: Boolean }) accessor selected = false;

  public static styles = [themeDefaultStyles, cssManager.defaultStyles, css`
    :host { display: flex; min-width: 0; font: 13px/1.5 var(--dees-font-family); color: var(--dees-color-text-primary); }
    * { box-sizing: border-box; }
    article { display: flex; flex-direction: column; width: 100%; min-width: 0; position: relative; background: var(--dees-color-bg-primary); border: 1px solid var(--dees-color-border-subtle); border-radius: var(--dees-radius-2xl); corner-shape: var(--dees-corner-shape); transition: border-color 140ms ease, box-shadow 140ms ease; }
    article.selectable { cursor: pointer; }
    article.selected { border-color: var(--dees-color-accent-primary); box-shadow: 0 0 0 1px var(--dees-color-accent-primary); }
    .product-image { display: grid; place-items: center; aspect-ratio: 4 / 3; margin: 8px 8px 0; min-width: 0; border-radius: var(--dees-radius-xl); corner-shape: var(--dees-corner-shape); background: var(--dees-color-bg-secondary); overflow: hidden; color: var(--dees-color-text-secondary); }
    .product-image img { display: block; width: 100%; height: 100%; object-fit: contain; }
    .selection { position: absolute; inset: 16px 16px auto auto; display: grid; place-items: center; min-width: 32px; min-height: 32px; border-radius: var(--dees-radius-md); background: var(--dees-color-bg-primary); box-shadow: var(--dees-shadow-xs); }
    .selection dees-input-checkbox { --dees-input-spacing: 0px; }
    .product-content { display: flex; flex-direction: column; flex: 1; gap: 8px; padding: 20px; }
    .category { color: var(--dees-color-text-secondary); font-size: 11px; font-weight: 500; }
    h3 { font-size: 19px; font-weight: 600; line-height: 1.25; letter-spacing: -.025em; margin: 0; overflow-wrap: anywhere; }
    .description { color: var(--dees-color-text-secondary); margin: 0; }
    .stock { display: flex; align-items: center; gap: 5px; color: var(--dees-color-text-secondary); font-size: 11px; margin-top: auto; padding-top: 8px; }
    .stock.unavailable { color: var(--dees-color-text-warning); }
    .product-footer { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 12px; padding: 0 20px 20px; }
    .price { display: grid; align-content: center; min-height: 40px; gap: 2px; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
    .current-price { font-size: 17px; line-height: 1.3; font-weight: 600; letter-spacing: -.02em; }
    .original-price { font-size: 12px; color: var(--dees-color-text-secondary); }
    .quantity { border-radius: var(--dees-radius-lg); background: var(--dees-color-fill-secondary); }
    @media (hover: hover) { article.selectable:hover:not(.selected) { border-color: var(--dees-color-border-strong); box-shadow: var(--dees-shadow-sm); } }
    @media (prefers-reduced-motion: reduce) { article { transition: none; } }
  `];

  public render(): TemplateResult {
    const product = this.productData;
    const available = product.inStock !== false;
    const currency = product.currency || '$';
    return html`<article class="${this.selectable ? 'selectable' : ''} ${this.selected ? 'selected' : ''}" aria-labelledby="product-name" @click=${this.toggleSelection}>
      <div class="product-image">${product.imageUrl ? html`<img src=${product.imageUrl} alt=${product.name} loading="lazy" decoding="async">` : html`<dees-icon .icon=${product.iconName || 'lucide:package'} iconSize="64"></dees-icon>`}</div>
      ${this.selectable ? html`<div class="selection" @click=${(event: Event) => event.stopPropagation()}>
        <dees-input-checkbox .ariaLabel=${`Select ${product.name}`} .value=${this.selected}
          @newValue=${(event: CustomEvent<boolean>) => this.setSelection(event.detail)}></dees-input-checkbox>
      </div>` : ''}
      <div class="product-content">${product.category ? html`<div class="category">${product.category}</div>` : ''}
        <h3 id="product-name">${product.name}</h3>${product.description ? html`<p class="description">${product.description}</p>` : ''}
        <div class="stock ${available ? '' : 'unavailable'}"><dees-icon .icon=${available ? 'lucide:check' : 'lucide:clock'} iconSize="12"></dees-icon>${product.stockText || (available ? 'In stock' : 'Out of stock')}</div>
      </div>
      <footer class="product-footer"><div class="price"><span class="current-price">${currency}${product.price.toFixed(2)}</span>
        ${product.originalPrice !== undefined && product.originalPrice > product.price ? html`<s class="original-price">${currency}${product.originalPrice.toFixed(2)}</s>` : ''}</div>
        ${this.showQuantitySelector ? html`<div class="quantity" role="group" aria-label=${`Quantity for ${product.name}`} @click=${(event: Event) => event.stopPropagation()}>
          <dees-input-quantityselector .value=${this.quantity}
            @newValue=${(event: CustomEvent<number>) => {
              this.quantity = event.detail;
              this.dispatchEvent(new CustomEvent('quantityChange', { detail: { quantity: this.quantity, productData: this.productData }, bubbles: true, composed: true }));
            }}></dees-input-quantityselector></div>` : ''}
      </footer>
    </article>`;
  }

  private toggleSelection(): void { if (this.selectable) this.setSelection(!this.selected); }
  private setSelection(selected: boolean): void {
    if (!this.selectable || this.selected === selected) return;
    this.selected = selected;
    this.dispatchEvent(new CustomEvent('selectionChange', { detail: { selected, productData: this.productData }, bubbles: true, composed: true }));
  }
}
